import { useCallback, useMemo, useState } from 'react';
import { useReactToPrint } from 'react-to-print';

const FORMAT_LABELS = {
  svg: 'SVG',
  png: 'PNG',
  pdf: 'PDF',
};

const useLabelPrint = ({ contentRef, format = 'svg', labelWidthMm, labelHeightMm }) => {
  const [error, setError] = useState(null);

  const hasDims =
    typeof labelWidthMm === 'number' &&
    typeof labelHeightMm === 'number' &&
    labelWidthMm > 0 &&
    labelHeightMm > 0;

  const pageStyle = useMemo(() => {
    if (!hasDims) return undefined;
    return `
      @page { size: ${labelWidthMm}mm ${labelHeightMm}mm; margin: 0; }
      @media print { body { margin: 0; } }
    `;
  }, [hasDims, labelWidthMm, labelHeightMm]);

  const reactToPrint = useReactToPrint({ contentRef, pageStyle });

  const print = useCallback(async () => {
    setError(null);

    if (format === 'svg' || format === 'png') {
      try {
        reactToPrint();
      } catch (err) {
        console.error(`[useLabelPrint] ${format} print failed`, err);
        setError(
          `We couldn't generate a ${FORMAT_LABELS[format] || format.toUpperCase()} for this label. Please try a different format or try again.`
        );
      }
      return;
    }

    if (format === 'pdf') {
      try {
        const { buildLabelPdf } = await import('../lib/labelPdf');

        const root = contentRef?.current;
        if (!root) throw new Error('No label content to print');

        const labelNodes = Array.from(root.children).filter((el) => el.nodeType === 1);
        if (labelNodes.length === 0) throw new Error('No labels to print');

        let widthMm = labelWidthMm;
        let heightMm = labelHeightMm;
        if (!hasDims) {
          const rect = labelNodes[0].getBoundingClientRect();
          widthMm = pxToMm(rect.width);
          heightMm = pxToMm(rect.height);
        }

        const blob = await buildLabelPdf({
          labelNodes,
          widthMm,
          heightMm,
        });

        await printBlobViaIframe(blob);
      } catch (err) {
        console.error('[useLabelPrint] PDF print failed', err);
        setError(
          `We couldn't generate a ${FORMAT_LABELS.pdf} for this label. Please try a different format or try again.`
        );
      }
      return;
    }

    setError(`Unknown print format: ${format}`);
  }, [contentRef, format, hasDims, labelWidthMm, labelHeightMm, reactToPrint]);

  const clearError = useCallback(() => setError(null), []);

  return { print, error, clearError };
};

const pxToMm = (px) => (px * 25.4) / 96;

const printBlobViaIframe = (blob) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    iframe.src = url;

    const cleanup = () => {
      try {
        iframe.remove();
      } catch {
        // ignore
      }
      URL.revokeObjectURL(url);
    };

    iframe.onload = () => {
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        setTimeout(cleanup, 60000);
        resolve();
      } catch (err) {
        cleanup();
        reject(err);
      }
    };

    iframe.onerror = (err) => {
      cleanup();
      reject(err);
    };

    document.body.appendChild(iframe);
  });

export default useLabelPrint;
